import EventEmitter from 'eventemitter3';
import { type DebouncedFunc, sumBy, throttle } from 'lodash-es';

import type { EventsMap } from '@iconik-io/core/src/events/types';
import type { IconikApiClientInterface } from '@iconik-io/iconik-api/src/client';
import type { AssetsAssetSchemaTypedWithId } from '@iconik-io/iconik-api/src/services/assets';
import {
  httpPatchJobsByJobId,
  httpPostJobs,
  type JobsJobSchema,
  type JobsJobSchemaWithId,
} from '@iconik-io/iconik-api/src/services/jobs';

import { CloseFileError, GenerateKeyframesError } from '../errors';
import type { IconikUploadScheduler } from '../scheduler';
import type { UploaderState } from '../state';
import type { KeyValueStorageInterface } from '../storage/key_value_store';
import type { LogEvents } from '../types';

const totalUploadedPercent = (state: UploaderState): number => {
  const totalUploaded = sumBy(state.chunks, ({ uploaded }) => uploaded);
  return Math.round((totalUploaded / state.fileInfo.size) * 100);
};

export interface JobIdStorageInterface
  extends KeyValueStorageInterface<string, string> {}

export type JobsServiceOptions = {
  clientName: string;
  clientVersion?: string;
};

/**
 * Monitors IconikUploadScheduler and creates and updates jobs in iconik based on the
 * events of the uploads.
 */
export class JobsService extends EventEmitter<EventsMap<LogEvents>> {
  private inFlightCreateJobRequests = new Map<
    string,
    Promise<JobsJobSchemaWithId>
  >();

  private throttledUpdaters = new Map<
    string,
    DebouncedFunc<(update: Partial<JobsJobSchema>) => Promise<void>>
  >();

  private enabled = false;

  constructor(
    private scheduler: IconikUploadScheduler,
    private client: IconikApiClientInterface,
    private jobIdStorage: JobIdStorageInterface,
    private options: JobsServiceOptions,
  ) {
    super();
  }

  /** Try to enable the service.  */
  enable = () => {
    if (this.enabled) {
      this.emit('log', {
        level: 'debug',
        message: 'Upload jobs service already enabled.',
      });
      return;
    }

    this.emit('log', {
      level: 'info',
      message: 'Enabling upload jobs service',
    });

    this.#registerForEvents();
    this.enabled = true;

    this.emit('log', {
      level: 'info',
      message: 'Upload jobs service enabled',
    });
  };

  /** Stop listening for events. No jobs will be created or updated. */
  disable = () => {
    if (!this.enabled) {
      this.emit('log', {
        level: 'debug',
        message: 'Upload jobs service already disabled.',
      });
      return;
    }

    this.#unregisterForEvents();
    this.throttledUpdaters.clear();
    this.enabled = false;

    this.emit('log', {
      level: 'info',
      message: 'Upload jobs service disabled',
    });
  };

  #registerForEvents = () => {
    this.scheduler
      .on('assetCreated', this.#onAssetCreated)
      .on('runUpload', this.#onRunUpload)
      .on('progress', this.#onProgress)
      .on('error', this.#onError)
      .on('cancelled', this.#onCancelled)
      .on('paused', this.#onPaused)
      .on('completed', this.#onCompleted);
  };

  #unregisterForEvents = () => {
    this.scheduler
      .removeListener('assetCreated', this.#onAssetCreated)
      .removeListener('runUpload', this.#onRunUpload)
      .removeListener('progress', this.#onProgress)
      .removeListener('error', this.#onError)
      .removeListener('cancelled', this.#onCancelled)
      .removeListener('paused', this.#onPaused)
      .removeListener('completed', this.#onCompleted);
  };

  #onAssetCreated = async (args: {
    state: UploaderState;
    asset: AssetsAssetSchemaTypedWithId;
  }) => {
    const { state, asset } = args;
    try {
      const jobRequest = this.client
        .request(
          httpPostJobs({
            object_type: 'assets',
            object_id: asset.id,
            type: 'TRANSFER',
            status: 'STARTED',
            title: `Upload ${asset.title}`,
            metadata: {
              storage_id: state.options.storageId,
              client_name: this.options.clientName,
              client_version: this.options.clientVersion,
            },
          }),
          {},
        )
        .then((response) => response.body);

      this.inFlightCreateJobRequests.set(state.id, jobRequest);

      const job = await jobRequest;
      this.jobIdStorage.set(state.id, job.id);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.emit('log', {
        level: 'warn',
        message: `Unable to create job for upload ${state.id}. Error: ${message}`,
      });
    } finally {
      this.inFlightCreateJobRequests.delete(state.id);
    }
  };

  #onRunUpload = ({ state }: { state: UploaderState }) =>
    this.#updateJob(
      state.id,
      {
        status: 'STARTED',
      },
      true,
    );

  #onProgress = ({ state }: { state: UploaderState }) => {
    const percent = totalUploadedPercent(state);
    this.#updateJob(state.id, { progress_processed: percent });
  };

  #onError = async (args: { state: UploaderState; error: Error }) => {
    const status =
      args.error instanceof GenerateKeyframesError ||
      args.error instanceof CloseFileError
        ? 'FINISHED_WITH_WARNING'
        : 'FAILED';
    await this.#updateJob(
      args.state.id,
      {
        status,
        error_message: args.error.message,
      },
      true,
    );
  };

  #onCancelled = async ({ state }: { state: UploaderState }) => {
    await this.#updateJob(
      state.id,
      {
        status: 'ABORTED',
      },
      true,
    );
    await this.jobIdStorage.delete(state.id);
  };

  #onPaused = async ({ state }: { state: UploaderState }) => {
    await this.#updateJob(state.id, { status: 'PAUSED' }, true);
  };

  #onCompleted = async ({ state }: { state: UploaderState }) => {
    await this.#updateJob(
      state.id,
      {
        progress_processed: 100,
        status: 'FINISHED',
      },
      true,
    );
    await this.jobIdStorage.delete(state.id);
  };

  #updateJob = async (
    stateId: string,
    update: Partial<JobsJobSchema>,
    flush = false,
  ) => {
    const throttler = this.#makeOrGetThrottledUpdaterForId(stateId);
    await throttler(update);

    if (flush) {
      await throttler.flush();
    }
  };

  #makeOrGetThrottledUpdaterForId = (stateId: string) => {
    const existing = this.throttledUpdaters.get(stateId);
    if (existing) {
      return existing;
    }

    const throttler = throttle(async (update: Partial<JobsJobSchema>) => {
      const jobId = await this.#getJobIdForState(stateId);
      if (!jobId) {
        return;
      }

      this.client
        .request(httpPatchJobsByJobId(jobId, update), {})
        .catch((error) => {
          const message =
            error instanceof Error ? error.message : 'Unknown error';
          this.emit('log', {
            level: 'warn',
            message: `Unable to update job for upload ${stateId}. Error: ${message}`,
          });
        });
    }, 5000);

    this.throttledUpdaters.set(stateId, throttler);

    return throttler;
  };

  #getJobIdForState = async (stateId: string) => {
    const existingJobId = await this.jobIdStorage.get(stateId);
    if (existingJobId) {
      return existingJobId;
    }
    const inFlightCall = this.inFlightCreateJobRequests.get(stateId);
    if (inFlightCall) {
      return inFlightCall.then(({ id }) => id).catch(() => undefined);
    }
    return undefined;
  };
}
